import { type ApolloLink } from '@apollo/client/core'
import { useContext } from 'react'

import {
  type CustomerAddressCreateMutation,
  type CustomerAddressCreateMutationVariables,
  type CustomerAddressDeleteMutation,
  type CustomerAddressDeleteMutationVariables,
  type CustomerAddressUpdateMutation,
  type CustomerAddressUpdateMutationVariables,
  type CustomerDefaultAddressUpdateMutation,
  type CustomerDefaultAddressUpdateMutationVariables,
  type MailingAddressFragmentFragment,
} from '@data/shopify/storefront/types'
import {
  CUSTOMER_ADDRESS_CREATE,
  CUSTOMER_ADDRESS_DELETE,
  CUSTOMER_ADDRESS_UPDATE,
  CUSTOMER_DEFAULT_ADDRESS_UPDATE,
} from '@data/shopify/storefront/mutations/customer'
import { useUser } from '@lib/auth'
import { type Locale } from '@lib/language'
import { type ParseResults } from '@lib/request'
import { ShopContext } from '@lib/shop-context'
import { type AddressFormValues, type UserAddress } from '@lib/user'
import { parseMailingAddressValues } from '../customer'
import { parseShopifyGlobalId } from '../helpers'
import { type ShopifyClient, mutateShopifyStorefront } from './client'
import { parseMutationResult } from './helpers'
import { LanguageContext } from '@lib/language-context'

/**
 * Gets address creation validation results.
 */
const parseCustomerAddressCreateResult = (
  customerAddressCreateResult: ApolloLink.Result<CustomerAddressCreateMutation>,
): ParseResults => {
  return parseMutationResult(
    customerAddressCreateResult.data?.customerAddressCreate
      ?.customerUserErrors ?? [],
  )
}

/**
 * Gets address updating validation results.
 */
const parseCustomerAddressUpdateResult = (
  customerAddressUpdateResult: ApolloLink.Result<CustomerAddressUpdateMutation>,
): ParseResults => {
  return parseMutationResult(
    customerAddressUpdateResult.data?.customerAddressUpdate
      ?.customerUserErrors ?? [],
  )
}

/**
 * Gets address deletion validation results.
 */
const parseCustomerAddressDeleteResult = (
  customerAddressDeleteResult: ApolloLink.Result<CustomerAddressDeleteMutation>,
): ParseResults => {
  return parseMutationResult(
    customerAddressDeleteResult.data?.customerAddressDelete
      ?.customerUserErrors ?? [],
  )
}

/**
 * Gets address setting as default validation results.
 */
const parseCustomerDefaultAddressUpdateResult = (
  customerDefaultAddressUpdateResult: ApolloLink.Result<CustomerDefaultAddressUpdateMutation>,
): ParseResults => {
  return parseMutationResult(
    customerDefaultAddressUpdateResult.data?.customerDefaultAddressUpdate
      ?.customerUserErrors ?? [],
  )
}

/**
 * Updates user's default address setting.
 */
const setAddressAsDefault = async (
  locale: Locale,
  shopifyStorefrontGraphQlClient: ShopifyClient,
  addressId: string,
  token: string,
) => {
  // Set the new address as the default
  const customerDefaultAddressUpdateResult = await mutateShopifyStorefront<
    CustomerDefaultAddressUpdateMutation,
    CustomerDefaultAddressUpdateMutationVariables
  >(locale, shopifyStorefrontGraphQlClient, CUSTOMER_DEFAULT_ADDRESS_UPDATE, {
    customerAccessToken: token,
    addressId,
  })

  return parseCustomerDefaultAddressUpdateResult(
    customerDefaultAddressUpdateResult,
  )
}

/**
 * Returns a method that creates a new user address.
 */
export const useCreateAddress = () => {
  const { user, mutateUser } = useUser()
  const { locale } = useContext(LanguageContext)
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  return async ({ isDefault, ...values }: AddressFormValues, token: string) => {
    try {
      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      const customerAddressCreateResult = await mutateShopifyStorefront<
        CustomerAddressCreateMutation,
        CustomerAddressCreateMutationVariables
      >(locale, shopifyStorefrontGraphQlClient, CUSTOMER_ADDRESS_CREATE, {
        customerAccessToken: token,
        address: values,
      })

      const createAddressResult = parseCustomerAddressCreateResult(
        customerAddressCreateResult,
      )
      const address =
        customerAddressCreateResult.data?.customerAddressCreate?.customerAddress

      if (address) {
        const addressFragment = address as MailingAddressFragmentFragment
        const addressFormValues = parseMailingAddressValues(addressFragment)

        if (isDefault) {
          // Set the new address as the default
          await setAddressAsDefault(
            locale,
            shopifyStorefrontGraphQlClient,
            addressFragment.id,
            token,
          )
        }

        // Mutate user with the new address list
        const addresses =
          user?.addresses?.map((address) => ({
            ...address,
            isDefault: !isDefault && !!address.isDefault,
          })) ?? []
        addresses.push({
          id: addressFragment.id,
          globalId: parseShopifyGlobalId(addressFragment.id),
          formatted: addressFragment.formatted,
          isDefault: !!isDefault,
          values: addressFormValues,
        })
        mutateUser({
          ...user,
          isLoggedIn: !!user?.isLoggedIn,
          addresses,
        })
      }

      return createAddressResult
    } catch (error) {
      console.log(error)

      return {
        fieldErrors: {},
        errorCount: 0,
        status: 'unknown_error',
      }
    }
  }
}

/**
 * Gets new default address.
 */
const getNewDefaultAddress = (
  addresses: UserAddress[],
  isDefault: boolean,
  globalId?: number,
) => {
  const otherAddresses = addresses.filter(
    (address) => address.globalId !== globalId,
  )

  if (isDefault || otherAddresses.length === 0) {
    return addresses.find((address) => address.globalId === globalId)
  }

  return (
    otherAddresses.find((address) => address.isDefault) ?? otherAddresses[0]
  )
}

/**
 * Returns a method that updates a user address.
 */
export const useUpdateAddress = () => {
  const { user, mutateUser } = useUser()
  const { locale } = useContext(LanguageContext)
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  return async (
    addressId: string,
    { isDefault, ...values }: AddressFormValues,
    token: string,
  ) => {
    try {
      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      const addressGlobalId = parseShopifyGlobalId(addressId)
      const customerAddressUpdateResult = await mutateShopifyStorefront<
        CustomerAddressUpdateMutation,
        CustomerAddressUpdateMutationVariables
      >(locale, shopifyStorefrontGraphQlClient, CUSTOMER_ADDRESS_UPDATE, {
        customerAccessToken: token,
        id: addressId,
        address: values,
      })

      const updateAddressResult = parseCustomerAddressUpdateResult(
        customerAddressUpdateResult,
      )
      const address =
        customerAddressUpdateResult.data?.customerAddressUpdate?.customerAddress

      if (address) {
        const addressFragment = address as MailingAddressFragmentFragment
        const userAddresses = user?.addresses ?? []
        const currentDefaultUserAddress = userAddresses.find(
          (userAddress) => userAddress.isDefault,
        )
        const newDefaultUserAddress = getNewDefaultAddress(
          userAddresses,
          isDefault,
          addressGlobalId,
        )
        const addressFormValues = parseMailingAddressValues(addressFragment)

        if (
          newDefaultUserAddress &&
          newDefaultUserAddress.globalId !== currentDefaultUserAddress?.globalId
        ) {
          // Set new default address
          await setAddressAsDefault(
            locale,
            shopifyStorefrontGraphQlClient,
            newDefaultUserAddress.id,
            token,
          )
        }

        // Mutate user with the new address list
        const addresses =
          user?.addresses?.map((userAddress) => {
            if (userAddress.globalId === addressGlobalId) {
              return {
                id: addressId,
                globalId: addressGlobalId,
                formatted: addressFragment.formatted,
                isDefault:
                  userAddress.globalId === newDefaultUserAddress?.globalId,
                values: addressFormValues,
              }
            }

            return {
              ...userAddress,
              isDefault:
                userAddress.globalId === newDefaultUserAddress?.globalId,
            }
          }) ?? []
        mutateUser({
          ...user,
          isLoggedIn: !!user?.isLoggedIn,
          addresses,
        })
      }

      return updateAddressResult
    } catch (error) {
      console.log(error)

      return {
        fieldErrors: {},
        errorCount: 0,
        status: 'unknown_error',
      }
    }
  }
}

/**
 * Returns a method that deletes a user address.
 */
export const useDeleteAddress = () => {
  const { user, mutateUser } = useUser()
  const { locale } = useContext(LanguageContext)
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  return async (addressId: string, token: string) => {
    try {
      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      const addressGlobalId = parseShopifyGlobalId(addressId)
      const customerAddressDeleteResult = await mutateShopifyStorefront<
        CustomerAddressDeleteMutation,
        CustomerAddressDeleteMutationVariables
      >(locale, shopifyStorefrontGraphQlClient, CUSTOMER_ADDRESS_DELETE, {
        customerAccessToken: token,
        id: addressId,
      })

      const deleteAddressResult = parseCustomerAddressDeleteResult(
        customerAddressDeleteResult,
      )

      if (
        deleteAddressResult.status === 'ok' &&
        deleteAddressResult.errorCount === 0
      ) {
        // Mutate user with the new address list
        const addresses =
          user?.addresses?.filter(
            (address) => address.globalId !== addressGlobalId,
          ) ?? []
        mutateUser({
          ...user,
          isLoggedIn: !!user?.isLoggedIn,
          addresses,
        })
      }

      return deleteAddressResult
    } catch (error) {
      console.log(error)

      return {
        fieldErrors: {},
        errorCount: 0,
        status: 'unknown_error',
      }
    }
  }
}
